import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { CalendarDays, Store, MapPin, Receipt as ReceiptIcon, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isInsufficientCreditsError } from "@/lib/edgeFunctionErrors";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  currency?: string;
  subtotal?: number;
  store_id?: string | null;
}

interface ReceiptDetailsProps {
  receipt: Receipt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReanalyze?: () => void;
}

const categoryColors = {
  food: "bg-category-food/20 text-category-food border-category-food/30",
  household: "bg-category-household/20 text-category-household border-category-household/30",
  health: "bg-category-health/20 text-category-health border-category-health/30",
  personal: "bg-category-personal/20 text-category-personal border-category-personal/30",
  other: "bg-category-other/20 text-category-other border-category-other/30",
};

const categoryIcons = {
  food: "🍎",
  household: "🏠",
  health: "💊",
  personal: "🧴",
  other: "📦",
};

export const ReceiptDetails = ({ receipt, open, onOpenChange, onReanalyze }: ReceiptDetailsProps) => {
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  if (!receipt) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleReanalyze = async () => {
    if (!receipt.imageUrl) {
      toast.error(t('receipt.noImageAvailable'));
      return;
    }

    setIsReanalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-receipt', {
        body: {
          receiptId: receipt.id,
          imageUrl: receipt.imageUrl
        }
      });

      if (error) throw error;

      // The credit is debited server-side by the analyze-receipt function; we
      // only refresh the balance display here.
      window.dispatchEvent(new Event('creditsUpdated'));

      toast.success(t('receipt.reAnalyzeSuccess'));
      onOpenChange(false);
      if (onReanalyze) onReanalyze();
    } catch (error) {
      // The server-side credit gate answered 402. That is an expected outcome,
      // not a failure: tell the user they're out of credits, and refresh the
      // balance display so the rest of the UI catches up.
      if (await isInsufficientCreditsError(error)) {
        window.dispatchEvent(new Event('creditsUpdated'));
        toast.error(t('uploadArea.insufficientBalance'), {
          description: t('uploadArea.insufficientBalanceDescription'),
        });
        return;
      }

      console.error('Error re-analyzing receipt:', error);
      toast.error(t('receipt.reAnalyzeError'));
    } finally {
      setIsReanalyzing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <ReceiptIcon className="h-6 w-6 text-primary" />
            {t('receipt.details')}
          </DialogTitle>
          <DialogDescription>
            {t('receipt.detailsDescription')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Re-analyze Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleReanalyze}
                disabled={isReanalyzing}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", isReanalyzing && "animate-spin")} />
                {t('receipt.reAnalyze')}
              </Button>
            </div>
            {/* Receipt Metadata */}
            <Card className="shadow-custom-md border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-2 text-xl font-semibold">
                      <Store className="h-5 w-5 text-primary" />
                      {receipt.store_id ? (
                        <span 
                          className="text-primary hover:underline cursor-pointer"
                          onClick={() => {
                            onOpenChange(false);
                            navigate(`/stores/${receipt.store_id}`);
                          }}
                        >
                          {receipt.store}
                        </span>
                      ) : (
                        <span>{receipt.store}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CalendarDays className="h-4 w-4" />
                      {formatDate(receipt.date)}
                    </div>

                    {receipt.address && (
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{receipt.address}</span>
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    <div className="text-sm text-muted-foreground mb-1">{t('table.total')}</div>
                    <div className="text-3xl font-bold text-primary">
                      {formatCurrency(receipt.total, receipt.currency)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="shadow-custom-md border-0">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <h3 className="text-lg font-semibold">
                      {t('receipt.itemsCount', { count: receipt.items.length })}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {receipt.items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start justify-between p-3 rounded-lg bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="font-medium">{item.name}</div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs",
                                categoryColors[item.category]
                              )}
                            >
                              {categoryIcons[item.category]} {item.category}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {t('receiptDetails.qty')}: {item.quantity}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              @ {formatCurrency(item.price, receipt.currency)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">
                            {formatCurrency(item.amount, receipt.currency)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="pt-4 border-t space-y-2">
                    {receipt.subtotal !== undefined && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('receiptDetails.subtotal')}:</span>
                        <span className="font-medium">
                          {formatCurrency(receipt.subtotal, receipt.currency)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t('receiptDetails.total')}:</span>
                      <span className="text-primary">{formatCurrency(receipt.total, receipt.currency)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Receipt Image - Moved to bottom */}
            {receipt.imageUrl && receipt.imageUrl !== '' && (
              <Card className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="text-sm font-medium mb-3 text-muted-foreground">{t('receiptDetails.receiptImage')}</div>
                  <img
                    src={receipt.imageUrl}
                    alt="Receipt"
                    className="w-full h-auto max-h-[600px] object-contain bg-muted rounded-lg"
                    onError={(e) => {
                      console.error('Failed to load image:', receipt.imageUrl);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
